import { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { dateToHumanFormat, getInitials, authStaff } from "../../utils/constants";
import useConfigOptions from "../../hooks/useConfigOptions";
import { selectAccessToken, selectAdminRole } from "../../slices/staffSlice";
import { show } from "../../slices/toastSlice";
import { fetchRequests } from "../../Requests/getRequests";
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreHorizontalCircle01Icon, EyeIcon, CancelCircleIcon, Delete01Icon } from '@hugeicons/core-free-icons';

const STAGE_STYLES = {
    pm:        'bg-indigo-50 text-indigo-600 ring-indigo-100',
    hoc:       'bg-blue-50 text-blue-600 ring-blue-100',
    crm:       'bg-fuchsia-50 text-fuchsia-600 ring-fuchsia-100',
    hor:       'bg-cyan-50 text-cyan-600 ring-cyan-100',
    active:    'bg-emerald-50 text-emerald-700 ring-emerald-100',
    completed: 'bg-sky-50 text-sky-700 ring-sky-100',
    approved:  'bg-teal-50 text-teal-700 ring-teal-100',
    default:   'bg-gray-100 text-gray-600 ring-gray-200',
};

function stageStyle(adminStage, state) {
    if (adminStage === 'true' && state === 'approved') return STAGE_STYLES.approved;
    if (adminStage === 'active' && state === 'active') return STAGE_STYLES.active;
    if (adminStage === 'completed' && state === 'completed') return STAGE_STYLES.completed;
    return STAGE_STYLES[adminStage] ?? STAGE_STYLES.default;
}

function stageLabel(adminStage, state) {
    if (adminStage === 'true' && state === 'approved')       return 'Facility Letter';
    if (adminStage === 'active' && state === 'active')       return 'Active';
    if (adminStage === 'completed' && state === 'completed') return 'Completed';
    return adminStage;
}

function TableRow({ item, path }) {
    const dispatch    = useDispatch();
    const accessToken = useSelector(selectAccessToken);
    const adminRole   = useSelector(selectAdminRole);
    const { loanStates, loanStatesOld } = useConfigOptions();

    const [dropOpen,     setDropOpen]     = useState(false);
    const [rejectOpen,   setRejectOpen]   = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [processing,   setProcessing]   = useState(false);
    // fixed position of the panel, calculated from button's screen rect
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });

    const btnRef    = useRef(null);
    const panelRef  = useRef(null);

    const close = useCallback(() => {
        setDropOpen(false);
        setRejectOpen(false);
    }, []);

    useEffect(() => {
        if (!dropOpen && !rejectOpen) return;
        const handler = (e) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target) &&
                btnRef.current   && !btnRef.current.contains(e.target)
            ) close();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [dropOpen, rejectOpen, close]);

    // close on scroll so the panel doesn't drift
    useEffect(() => {
        if (!dropOpen && !rejectOpen) return;
        window.addEventListener("scroll", close, true);
        return () => window.removeEventListener("scroll", close, true);
    }, [dropOpen, rejectOpen, close]);

    const openDrop = () => {
        const rect = btnRef.current.getBoundingClientRect();
        // panel width 176px — shift left if too close to right edge
        const panelW = 176;
        const left = rect.right - panelW < 0 ? rect.left : rect.right - panelW;
        setDropPos({ top: rect.bottom + 6, left });
        setRejectOpen(false);
        setDropOpen(o => !o);
    };

    const openReject = () => {
        const rect = btnRef.current.getBoundingClientRect();
        const panelW = 256;
        const left = rect.right - panelW < 0 ? rect.left : rect.right - panelW;
        setDropPos({ top: rect.bottom + 6, left });
        setDropOpen(false);
        setRejectOpen(true);
    };

    const canActOn = item?.state !== loanStates.active && item?.state !== loanStates.completed;
    const canDelete = canActOn;

    const linkTo =
        item?.state === loanStatesOld.initial   ? `/${item?.type.toLowerCase()}/${item._id}` :
        item?.state === loanStatesOld.approved  ? `/approved/${item?.type.toLowerCase()}/${item._id}` :
        item?.state === loanStatesOld.active    ? `/active/${item._id}` :
        item?.state === loanStatesOld.completed ? `/completed/${item._id}` :
        `/rejected/${item._id}`;

    const handleReject = async () => {
        if (!rejectReason.trim()) {
            dispatch(show({ type: "error", state: true, message: "Please provide a reason for rejection" }));
            return;
        }
        setProcessing(true);
        const res = await fetchRequests(`/loans/staff/rejectRejectLoan`, "POST", authStaff(accessToken), {}, {
            _id: item._id,
            type: item.type,
            approved: false,
            overallAssessment: "Rejected",
            comments: rejectReason,
            adminRole,
            reason: rejectReason,
            dateTime: new Date().toISOString(),
        });
        setProcessing(false);
        close();
        if (res?.err) { dispatch(show({ type: "error", state: true, message: res.err })); return; }
        dispatch(show({ type: "success", state: true, message: "Loan rejected successfully" }));
        setTimeout(() => window.location.reload(), 800);
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete this loan application for ${item?.client?.otherNames} ${item?.client?.surname}? This cannot be undone.`)) return;
        setProcessing(true);
        const res = await fetchRequests(`/loans/staff/loan`, "DELETE", authStaff(accessToken), { _id: item._id, loanType: item.type }, {});
        setProcessing(false);
        close();
        if (res?.err) { dispatch(show({ type: "error", state: true, message: res.err })); return; }
        dispatch(show({ type: "success", state: true, message: "Loan application deleted" }));
        setTimeout(() => window.location.reload(), 800);
    };

    return (
        <>
        <tr className="hover:bg-gray-50/60 dark:hover:bg-gray-700/30 transition-colors">
            <td className='px-5 py-3.5'>
                <div className='flex items-center gap-2 flex-wrap'>
                    <span className='text-sm font-medium text-gray-900 dark:text-gray-100'>
                        {item?.client?.otherNames} {item?.client?.surname}
                    </span>
                    {item?.state !== "active" && item?.relManager && (
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
                            {getInitials(item?.relManager)}
                        </span>
                    )}
                    {item?.state !== loanStates.completed && item?.daysInDelinq >= 1 && (
                        <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-200">
                            {item?.daysInDelinq}d arrears
                        </span>
                    )}
                </div>
            </td>
            <td className='px-5 py-3.5 text-sm text-gray-600 dark:text-gray-400'>{item?.phone}</td>
            <td className='px-5 py-3.5 text-sm text-gray-600 dark:text-gray-400'>
                {(path === "active-loans" || path === "completed-loans") ? item?.type : item?.amountReq}
            </td>
            <td className='px-5 py-3.5 text-sm text-gray-600 dark:text-gray-400'>{item?.amount}</td>
            <td className='px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400'>
                {(path === "active-loans" || path === "completed-loans")
                    ? dateToHumanFormat(item?.dateDisbursed)
                    : dateToHumanFormat(item?.createdAt)}
            </td>
            <td className='px-5 py-3.5'>
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium uppercase ring-1 ring-inset ${stageStyle(item?.adminStage, item?.state)}`}>
                    {stageLabel(item?.adminStage, item?.state)}
                </span>
            </td>
            <td className='px-5 py-3.5'>
                <button
                    ref={btnRef}
                    onClick={openDrop}
                    disabled={processing}
                    className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                    <HugeiconsIcon icon={MoreHorizontalCircle01Icon} size={20} />
                </button>
            </td>
        </tr>

        {/* dropdown — rendered via fixed position, outside any overflow container */}
        {dropOpen && (
            <tr style={{ display: 'contents' }}>
                <td style={{ display: 'contents' }}>
                    <div
                        ref={panelRef}
                        style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999, width: 176 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 py-1.5 overflow-hidden"
                    >
                        <Link
                            to={linkTo}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            onClick={close}
                        >
                            <HugeiconsIcon icon={EyeIcon} size={16} className="text-gray-400 shrink-0" />
                            View
                        </Link>

                        {canActOn && (
                            <button
                                onClick={openReject}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-amber-600 hover:bg-amber-50 transition-colors"
                            >
                                <HugeiconsIcon icon={CancelCircleIcon} size={16} className="shrink-0" />
                                Reject
                            </button>
                        )}

                        {canDelete && (
                            <>
                                <div className="my-1 border-t border-gray-100" />
                                <button
                                    onClick={handleDelete}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    <HugeiconsIcon icon={Delete01Icon} size={16} className="shrink-0" />
                                    Delete
                                </button>
                            </>
                        )}
                    </div>
                </td>
            </tr>
        )}

        {/* reject reason panel */}
        {rejectOpen && (
            <tr style={{ display: 'contents' }}>
                <td style={{ display: 'contents' }}>
                    <div
                        ref={panelRef}
                        style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999, width: 256 }}
                        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-4"
                    >
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Reject Application</p>
                        <textarea
                            rows={3}
                            className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg p-2.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 resize-none placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Reason for rejection…"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            autoFocus
                        />
                        <div className="flex gap-2 mt-3">
                            <button
                                onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                                className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={processing}
                                className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-60"
                            >
                                {processing ? 'Rejecting…' : 'Confirm'}
                            </button>
                        </div>
                    </div>
                </td>
            </tr>
        )}
        </>
    );
}

export default TableRow;
